import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CommunityDTO } from "utils/helpers/models/shared/community.dto";
import dpIcon from "assets/images/others/host-side-images/dpIcon.png";
import learnlocalLoader from "assets/images/gifs/learnlocalLoader.gif";
import useCommunityHooks from "../useHooks";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";

type SidebarProps = {
  activeCommunityId?: number | null;
  onSelect: (community: CommunityDTO) => void;
  searchTitle?: string;
  onCommunitiesChange?: (count: number) => void;
  onLoadingChange?: (isLoading: boolean) => void;
};

const PAGE_SIZE = 15;

const Sidebar: React.FC<SidebarProps> = ({
  activeCommunityId,
  onSelect,
  searchTitle = "",
  onCommunitiesChange,
  onLoadingChange,
}) => {
  const { getCommunities } = useCommunityHooks();

  const [communities, setCommunities] = useState<CommunityDTO[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef(false);

  const isActive = useCallback(
    (c: CommunityDTO) =>
      (activeCommunityId ?? null) === (c?.eventData?.eventId ?? null),
    [activeCommunityId]
  );

  const loadCommunities = useCallback(
    async (pageNum: number, reset: boolean = false, title: string = "") => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setIsLoadingMore(true);
      if (onLoadingChange) onLoadingChange(true);
      try {
        const queryParams: any = { page: pageNum, size: PAGE_SIZE };
        if (title.trim()) queryParams.searchTitle = title.trim();

        let loadedCount = 0;
        await getCommunities((newCommunities: CommunityDTO[]) => {
          loadedCount = newCommunities.length;
          if (reset) {
            setCommunities(newCommunities);
            if (onCommunitiesChange) onCommunitiesChange(newCommunities.length);
          } else {
            setCommunities((prev) => {
              const existingIds = new Set(
                prev.map((c) => c.eventData?.eventId)
              );
              const filtered = newCommunities.filter(
                (c) => !existingIds.has(c.eventData?.eventId)
              );
              const updated = [...prev, ...filtered];
              if (onCommunitiesChange) onCommunitiesChange(updated.length);
              return updated;
            });
          }
        }, queryParams);

        setHasMore(loadedCount === PAGE_SIZE);
        setPage(pageNum + 1);
      } catch (e) {
        setHasMore(false);
        if (onCommunitiesChange) onCommunitiesChange(0);
      } finally {
        setIsLoadingMore(false);
        if (onLoadingChange) onLoadingChange(false);
        isFetchingRef.current = false;
      }
    },
    [getCommunities, onCommunitiesChange, onLoadingChange]
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (isLoadingMore || !hasMore) return;
      const threshold = 50;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
        loadCommunities(page, false, searchTitle);
      }
    },
    [hasMore, isLoadingMore, loadCommunities, page, searchTitle]
  );

  useEffect(() => {
    setCommunities([]);
    setPage(0);
    setHasMore(true);
    loadCommunities(0, true, searchTitle);
  }, [searchTitle, loadCommunities]);

  // Notify parent when communities change
  useEffect(() => {
    if (onCommunitiesChange) {
      onCommunitiesChange(communities.length);
    }
  }, [communities.length, onCommunitiesChange]);

  const items = useMemo(() => communities, [communities]);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="w-full md:w-[300px] lg:w-[329px] bg-white overflow-y-auto overflow-x-hidden custom-scrollbar h-full flex flex-col min-h-0"
    >
      <h1 className="text-xl text-blackie font-bold mb-4 px-1 flex-shrink-0">All Communities</h1>
      <div className="space-y-0 flex-1 min-h-0">
        {items.length > 0 &&
          items.map((item, i) => (
            <div
              key={item.eventData?.eventId || i}
              className={`flex items-center justify-between cursor-pointer pr-3 pl-1 py-3.5 bordr-linear transition-colors ${
                isActive(item) ? "bg-fade" : "hover:bg-gray-50"
              }`}
              onClick={() => onSelect(item)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onSelect(item);
              }}
              tabIndex={0}
              role="button"
              aria-label={`Open chat for ${item.eventData?.title}`}
            >
              <div className="flex items-center space-x-3 min-w-0 flex-1">
                <img
                  src={
                    concatPathWithBackendUrl(item?.eventData?.eventPics?.[0]) ||
                    dpIcon
                  }
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex flex-col gap-1 min-w-0 flex-1">
                  <p className="text-sm text-blackie font-medium truncate">
                    {item?.eventData?.title}
                  </p>
                  <p className="text-xs text-labelTextColor truncate break-words">
                    {item?.lastMessage}
                  </p>
                </div>
              </div>
            </div>
          ))}

        {/* {isLoadingMore && (
          <div className="flex justify-center py-4">
            <img
              src={learnlocalLoader}
              className="w-10 h-10"
              alt="Loading..."
            />
          </div>
        )} */}
      </div>
    </div>
  );
};

export default React.memo(Sidebar);
